import { Action, Reducer } from 'redux';
import { fork, call, takeEvery, put, all, select, delay } from 'redux-saga/effects';
import { replace } from 'connected-react-router';

import { Login } from '../models/forms/Login';
import LoginResponse from '../models/LoginResponse';
import Api from '../api';

import { decode } from 'jsonwebtoken';
import UserDetails from '../models/UserDetails';

import { IRootState } from './index';
import ErrorResponse from '../models/ErrorResponse';
import { UserRoles } from '../models/UserRole';
import SignalRHandler from 'src/messaging/SignalRHandler';

// import { selectors as authSelectors } from '../reducers/Auth';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import DialogList from 'src/dialogs';
import { UserTypes } from 'src/models/UserType';
import Authorization from 'src/util/Authorization';

// tslint:disable no-any

// Store
export interface IAuthState {
    login: {
        isLoggingIn: boolean;
        error: string;
    };
    user: {
        jwt: string;
        loggedIn: boolean;
        username: string;
        email: string;
        userId: number;
        isAdmin: boolean;
        role: UserRoles;
        type: UserTypes;
        passwordConfirmed: boolean;
        twoFactorEnabled: boolean;
    };
    passwordResetInfo: Login;
}

const initialState: IAuthState = {
    login: {
        isLoggingIn: false,
        error: '',
    },
    user: {
        jwt: '',
        loggedIn: false,
        username: '',
        email: '',
        userId: 0,
        isAdmin: false,
        passwordConfirmed: false,
        role: undefined,
        type: undefined,
        twoFactorEnabled: false,
    },
    passwordResetInfo: {
        username: '',
        password: '',
        persistSession: false,
    },
};

// Action Types
export enum actionTypes {
    LOGIN = '[auth] LOGIN',
    LOGIN_SUCCESS = '[auth] LOGIN_SUCCESS',
    LOGIN_FAIL = '[auth] LOGIN_FAIL',
    LOGOUT = '[auth] LOGOUT',
    CONFIRM = '[auth] CONFIRM',
    CONFIRM_SUCCESS = '[auth] CONFIRM_SUCCESS',
    CONFIRM_FAIL = '[auth] CONFIRM_FAIL',
    FORGOT_PASSWORD = '[auth] FORGOT_PASSWORD',
    CHANGE_ROLE = '[auth] CHANGE_ROLE',
    TRACK_TWO_FACTOR_JWT = '[auth] TRACK_TWO_FACTOR_JWT',
    LOGIN_TWO_FACTOR = '[auth] LOGIN_TWO_FACTOR',
    LOGIN_TWO_FACTOR_SUCCESS = '[auth] LOGIN_TWO_FACTOR_SUCCESS',
    LOGIN_TWO_FACTOR_FAILURE = '[auth] LOGIN_TWO_FACTOR_FAILURE',
    UPDATE_PASSWORD_RESET_INFO = '[auth UPDATE_PASSWORD_RESET_INFO',
    CANCEL_LOGIN = '[auth] CANCEL_LOGIN',
    RESET_EXPIRED_PASSWORD = '[auth] RESET_EXPIRED_PASSWORD',
}

// Action Creator
export const actionCreators = {
    login: (loginRequest: Login) => {
        return { type: actionTypes.LOGIN, payload: loginRequest };
    },
    loginSuccess: (loginResponse: LoginResponse) => {
        return { type: actionTypes.LOGIN_SUCCESS, payload: loginResponse };
    },
    loginFailure: (errorResposne: ErrorResponse) => {
        return { type: actionTypes.LOGIN_FAIL, payload: errorResposne };
    },
    logout: () => {
        return { type: actionTypes.LOGOUT };
    },
    forgotPassword: (email: string) => {
        return { type: actionTypes.FORGOT_PASSWORD, payload: email };
    },
    changeRole: (role: UserRoles) => {
        return { type: actionTypes.CHANGE_ROLE, payload: role };
    },
    trackTwoFactorJwt: (jwt: string) => {
        return { type: actionTypes.TRACK_TWO_FACTOR_JWT, payload: jwt };
    },
    loginTwoFactor: (payload: { twoFactorAuthPin: string; persistSession: boolean }) => {
        return { type: actionTypes.LOGIN_TWO_FACTOR, payload };
    },
    loginTwoFactorFailure: () => {
        return { type: actionTypes.LOGIN_TWO_FACTOR_FAILURE };
    },
    updatePasswordResetInfo: (info: { username: string; password: string }) => {
        return { type: actionTypes.UPDATE_PASSWORD_RESET_INFO, payload: info };
    },
    cancelLogin: () => {
        return { type: actionTypes.CANCEL_LOGIN };
    },
    resetExpiredPassword: (password: string) => {
        return { type: actionTypes.RESET_EXPIRED_PASSWORD, payload: password };
    },
};

// Reducer
const reducer: Reducer<IAuthState> = (state: IAuthState = initialState, action: Action): IAuthState => {
    switch (action.type) {
        case actionTypes.LOGIN: {
            const loginState = {
                ...state.login,
                isLoggingIn: true,
                error: '',
            };
            return {
                ...state,
                login: loginState,
                user: initialState.user,
            };
        }
        case actionTypes.LOGIN_SUCCESS: {
            const loginState = {
                ...state.login,
                isLoggingIn: false,
            };
            const payload = (action as { type: string; payload: LoginResponse }).payload;
            const decoded = decode(payload.jwt);
            /* tslint:disable:no-string-literal */
            const userState = {
                ...state.user,
                jwt: payload.jwt,
                username: decoded ? decoded['un'] : '',
                email: decoded ? decoded['em'] : '',
                userId: decoded ? decoded['id'] : 0,
                isAdmin: decoded ? decoded['sa'] : false,
                loggedIn: true,
                role: decoded ? Authorization.JwtUserRole(decoded['rol']) : undefined,
                type: decoded ? Authorization.JwtUserType(decoded['typ']) : undefined,
                twoFactorEnabled: decoded && decoded['tfe'],
                ownershipAccess: decoded && decoded['oa'],
                contractAccess: decoded && decoded['ca'],
                reportAccess: decoded && decoded['ra']
            };
            /* tslint:enable:no-string-literal */
            return {
                ...state,
                login: loginState,
                user: userState,
                passwordResetInfo: initialState.passwordResetInfo,
            };
        }
        case actionTypes.CHANGE_ROLE: {
            const role = (action as { type: string; payload: UserRoles }).payload;
            return {
                ...state,
                user: {
                    ...state.user,
                    role: role,
                },
            };
        }
        case actionTypes.LOGIN_FAIL: {
            const payload = (action as { type: string; payload: { message: string; messageCode: string } }).payload;
            const loginState = {
                ...state.login,
                isLoggingIn: false,
                error: payload.message,
            };
            return {
                ...state,
                login: loginState,
                passwordResetInfo: initialState.passwordResetInfo,
            };
        }
        case actionTypes.LOGOUT: {
            return {
                ...state,
                user: initialState.user,
                passwordResetInfo: initialState.passwordResetInfo,
            };
        }
        case actionTypes.TRACK_TWO_FACTOR_JWT: {
            const jwt = (action as { type: string; payload: string }).payload;
            const user = {
                ...initialState.user,
                jwt,
            };
            return {
                ...state,
                user,
            };
        }
        case actionTypes.LOGIN_TWO_FACTOR_FAILURE: {
            const login = {
                ...state.login,
                error: 'Invalid Pin Entered',
            };
            return {
                ...state,
                login,
            };
        }
        case actionTypes.UPDATE_PASSWORD_RESET_INFO: {
            const info = (action as { type: string; payload: Login }).payload;
            return {
                ...state,
                passwordResetInfo: info,
            };
        }
        case actionTypes.CANCEL_LOGIN: {
            return {
                ...state,
                login: initialState.login,
                passwordResetInfo: initialState.passwordResetInfo,
                user: initialState.user,
            };
        }
        default:
            return state;
    }
};

export default reducer;

export const selectors = {
    user: (state: IRootState): UserDetails => state.auth.user,
    isLoggingIn: (state: IRootState): boolean => state.auth.login.isLoggingIn,
    isLoggedIn: (state: IRootState): boolean => state.auth.user.loggedIn,
    jwt: (state: IRootState): string => state.auth.user.jwt,
    error: (state: IRootState): string => state.auth.login.error,
    passwordResetInfo: (state: IRootState) => state.auth.passwordResetInfo,
};

// Side-Effects
const sagas = {
    login: function* (action: { type: string; payload: Login }): unknown {
        const [result] = yield all([call(Api.Auth.login, action.payload.username, action.payload.password), delay(200)]);
        if (result instanceof ErrorResponse) {
            if (result.messageCode === '11') {
                yield put(actionCreators.updatePasswordResetInfo(action.payload));
                yield put(layoutActionCreators.showDialog(DialogList.UPDATE_PASSWORD));
            } else {
                yield put(actionCreators.loginFailure(result));
            }
        } else {
            const encodedTokenClaims = result.jwt.split('.')[1];
            const tokenClaims = JSON.parse(window.atob(encodedTokenClaims.replace('-', '+').replace('_', '/')));

            if (tokenClaims.tfj) {
                yield put(actionCreators.trackTwoFactorJwt(result.jwt));
                yield put(layoutActionCreators.showDialog('loginTwoFactor'));
            } else {
                result.persistSession = action.payload.persistSession;
                yield put(actionCreators.loginSuccess(result));
                yield put(replace('/'));
            }
        }
    },
    loginSuccess: function* (action: { type: string; payload: { jwt: string; persistSession: boolean } }) {
        const jwt = yield select(selectors.jwt);
        if (action.payload.persistSession) {
            localStorage.setItem('jwt', action.payload.jwt);
        } else {
            sessionStorage.setItem('jwt', action.payload.jwt);
        }

        const baseUrl = process.env.REACT_APP_MESSAGES_URL;

        yield call(SignalRHandler.Configure, baseUrl, jwt);
    },
    logout: function* (action: Action) {
        localStorage.removeItem('jwt');
        sessionStorage.removeItem('jwt');
        yield put(replace('/login'));
    },
    forgotPassword: function* (action: { type: string; payload: string }): any {
        const [result] = yield all([call(Api.Auth.forgotPassword, action.payload), delay(200)]);

        if (result instanceof ErrorResponse) {
            console.log('forgotPassword failed, from the auth reducer', result);
        } else {
            console.log('forgotPassword did not return errorResponse, from the auth reducer');
        }
    },
    loginTwoFactor: function* (action: { type: string; payload: { twoFactorAuthPin: string; persistSession: boolean } }): any {
        const jwt = yield select(selectors.jwt);
        const [result] = yield all([call(Api.Auth.loginTwoFactor, action.payload.twoFactorAuthPin, jwt), delay(200)]);

        if (result instanceof ErrorResponse) {
            yield put(actionCreators.loginTwoFactorFailure());
        } else {
            result.persistSession = action.payload.persistSession;
            yield put(actionCreators.loginSuccess(result));
            yield put(layoutActionCreators.hideDialog());
        }
    },
    resetExpiredPassword: function* (action: { type: string; payload: string }) {
        const userInfo = yield select(selectors.passwordResetInfo);
        const resetPasswordInfo = {
            username: userInfo.username,
            password: userInfo.password,
            newPassword: action.payload,
        };

        const result = yield call(Api.Auth.resetExpiredPassword, resetPasswordInfo);

        if (result instanceof ErrorResponse) {
            yield put(layoutActionCreators.hideDialog());
        } else {
            result.persistSession = userInfo.persistSession;
            yield put(actionCreators.loginSuccess(result));
            yield put(layoutActionCreators.hideDialog());
        }
    },
};

const watches = {
    login: function* () {
        yield takeEvery(actionTypes.LOGIN, sagas.login);
    },
    loginSuccess: function* () {
        yield takeEvery(actionTypes.LOGIN_SUCCESS, sagas.loginSuccess);
    },
    logout: function* () {
        yield takeEvery(actionTypes.LOGOUT, sagas.logout);
    },
    forgotPassword: function* () {
        yield takeEvery(actionTypes.FORGOT_PASSWORD, sagas.forgotPassword);
    },
    loginTwoFactor: function* () {
        yield takeEvery(actionTypes.LOGIN_TWO_FACTOR, sagas.loginTwoFactor);
    },
    resetExpiredPassword: function* () {
        yield takeEvery(actionTypes.RESET_EXPIRED_PASSWORD, sagas.resetExpiredPassword);
    },
};

export function* saga() {
    yield fork(watches.login);
    yield fork(watches.loginSuccess);
    yield fork(watches.logout);
    yield fork(watches.forgotPassword);
    yield fork(watches.loginTwoFactor);
    yield fork(watches.resetExpiredPassword);
}



// WEBPACK FOOTER //
// ./src/reducers/Auth.ts